import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Search, 
  Filter, 
  Download, 
  Upload,
  Play,
  Pause,
  Edit2,
  Trash2,
  TrendingUp,
  TrendingDown,
  AlertCircle,
  CheckCircle,
  Plus,
  Minus,
  DollarSign,
  Target,
  Eye,
  MousePointer,
  BarChart3,
  Zap,
  Shield,
  X,
  ChevronUp,
  ChevronDown,
  RefreshCw,
  Settings,
  Sparkles,
  AlertTriangle
} from 'lucide-react';

interface Keyword {
  id: string;
  adGroupId: string;
  adGroupName: string;
  text: string;
  matchType: 'BROAD' | 'PHRASE' | 'EXACT';
  status: 'ENABLED' | 'PAUSED' | 'REMOVED';
  bid: number;
  effectiveBid: number;
  qualityScore: number;
  impressions: number;
  clicks: number;
  cost: number;
  ctr: number;
  avgCpc: number;
  conversions: number;
  conversionRate: number;
}

interface KeywordsManagerProps {
  campaignId: string;
  campaignName?: string;
  onRefresh?: () => void;
}

const KeywordsManager: React.FC<KeywordsManagerProps> = ({ 
  campaignId, 
  campaignName = 'Kampania',
  onRefresh 
}) => {
  const [keywords, setKeywords] = useState<Keyword[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedKeywords, setSelectedKeywords] = useState<Set<string>>(new Set());
  const [filterStatus, setFilterStatus] = useState<'all' | 'enabled' | 'paused'>('all');
  const [sortBy, setSortBy] = useState<'impressions' | 'clicks' | 'ctr' | 'cost' | 'conversions'>('impressions');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [editingKeyword, setEditingKeyword] = useState<string | null>(null);
  const [newBid, setNewBid] = useState<number>(0);
  const [showAddModal, setShowAddModal] = useState(false);
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  // Pobierz słowa kluczowe
  useEffect(() => {
    loadKeywords();
  }, [campaignId]);

  const loadKeywords = async () => {
    try {
      setLoading(true);
      const response = await fetch(
        `https://api.plexisystem.pl/api/google-ads/campaigns/${campaignId}/keywords`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        // Backend zwraca tablicę bezpośrednio
        const keywordsData = Array.isArray(data) ? data : (data.keywords || []);
        
        // Mapuj dane do odpowiedniego formatu
        const formattedKeywords = keywordsData.map(k => ({
          id: k.id,
          adGroupId: k.adGroupId || 'default',
          adGroupName: k.adGroupName || 'Domyślna grupa',
          text: k.keyword || k.text || '',
          matchType: k.matchType || 'BROAD',
          status: k.status || 'ENABLED',
          bid: k.bidAmount || k.bid || 0,
          effectiveBid: k.bidAmount || k.bid || 0,
          qualityScore: k.qualityScore || 7,
          impressions: k.impressions || 0,
          clicks: k.clicks || 0,
          cost: k.cost || 0,
          ctr: k.ctr || 0,
          avgCpc: k.avgCpc || 0,
          conversions: k.conversions || 0,
          conversionRate: k.conversionRate || (k.conversions && k.clicks ? (k.conversions / k.clicks * 100) : 0)
        }));
        
        console.log('📝 Loaded keywords:', formattedKeywords.length);
        setKeywords(formattedKeywords);
      }
    } catch (error) {
      console.error('Error loading keywords:', error);
    } finally {
      setLoading(false);
    }
  };

  // Filtrowanie i sortowanie
  const filteredAndSortedKeywords = useMemo(() => {
    let filtered = keywords.filter(kw => {
      const matchesSearch = kw.text.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesStatus = filterStatus === 'all' || 
        (filterStatus === 'enabled' && kw.status === 'ENABLED') ||
        (filterStatus === 'paused' && kw.status === 'PAUSED');
      return matchesSearch && matchesStatus;
    });

    filtered.sort((a, b) => {
      const aVal = a[sortBy];
      const bVal = b[sortBy];
      const modifier = sortOrder === 'asc' ? 1 : -1;
      return aVal > bVal ? modifier : -modifier;
    });

    return filtered;
  }, [keywords, searchTerm, filterStatus, sortBy, sortOrder]);

  // Statystyki
  const stats = useMemo(() => {
    const enabled = keywords.filter(k => k.status === 'ENABLED');
    return {
      total: keywords.length,
      enabled: enabled.length,
      paused: keywords.length - enabled.length,
      totalCost: keywords.reduce((sum, k) => sum + k.cost, 0),
      totalClicks: keywords.reduce((sum, k) => sum + k.clicks, 0),
      totalConversions: keywords.reduce((sum, k) => sum + k.conversions, 0),
      avgCtr: keywords.length ? keywords.reduce((sum, k) => sum + k.ctr, 0) / keywords.length : 0,
      avgCpc: keywords.length ? keywords.reduce((sum, k) => sum + k.avgCpc, 0) / keywords.length : 0
    };
  }, [keywords]);

  // Akcje na słowach kluczowych
  const updateKeywordBid = async (keyword: Keyword, newBid: number) => {
    try {
      setActionLoading(keyword.id);
      const response = await fetch(
        `https://api.plexisystem.pl/api/google-ads/keywords/${keyword.adGroupId}/${keyword.id}/bid`,
        {
          method: 'PATCH',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ newBid })
        }
      );
      
      if (response.ok) {
        await loadKeywords();
        setEditingKeyword(null);
      }
    } catch (error) {
      console.error('Error updating bid:', error);
    } finally {
      setActionLoading(null);
    }
  };

  const toggleKeywordStatus = async (keyword: Keyword) => {
    try {
      setActionLoading(keyword.id);
      const endpoint = keyword.status === 'ENABLED' ? 'pause' : 'enable';
      const response = await fetch(
        `https://api.plexisystem.pl/api/google-ads/keywords/${keyword.adGroupId}/${keyword.id}/${endpoint}`,
        {
          method: 'PATCH',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      if (response.ok) {
        await loadKeywords();
      }
    } catch (error) {
      console.error('Error toggling status:', error);
    } finally {
      setActionLoading(null);
    }
  };

  const addNegativeKeyword = async (keywordText: string) => {
    try {
      const response = await fetch(
        `https://api.plexisystem.pl/api/google-ads/campaigns/${campaignId}/negative-keywords`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ 
            keyword: keywordText,
            matchType: 'BROAD'
          })
        }
      );
      
      if (response.ok) {
        await loadKeywords();
      }
    } catch (error) {
      console.error('Error adding negative keyword:', error);
    }
  };

  // Bulk actions
  const bulkPause = async () => {
    for (const id of selectedKeywords) {
      const keyword = keywords.find(k => k.id === id);
      if (keyword && keyword.status === 'ENABLED') {
        await toggleKeywordStatus(keyword);
      }
    }
    setSelectedKeywords(new Set());
  };

  const bulkEnable = async () => {
    for (const id of selectedKeywords) {
      const keyword = keywords.find(k => k.id === id);
      if (keyword && keyword.status === 'PAUSED') {
        await toggleKeywordStatus(keyword);
      }
    }
    setSelectedKeywords(new Set());
  };

  const exportKeywords = () => {
    const csv = [
      ['Słowo kluczowe', 'Dopasowanie', 'Status', 'Stawka', 'Wyświetlenia', 'Kliknięcia', 'CTR', 'CPC', 'Koszt', 'Konwersje'],
      ...filteredAndSortedKeywords.map(k => [
        k.text,
        k.matchType,
        k.status,
        k.bid.toFixed(2),
        k.impressions,
        k.clicks,
        k.ctr.toFixed(2) + '%',
        k.avgCpc.toFixed(2),
        k.cost.toFixed(2),
        k.conversions
      ])
    ].map(row => row.join(',')).join('\n');
    
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `keywords-${campaignName}-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const getQualityScoreColor = (score: number) => {
    if (score >= 7) return 'text-green-400';
    if (score >= 4) return 'text-yellow-400';
    return 'text-red-400';
  };

  const getPerformanceIcon = (ctr: number) => {
    if (ctr > 5) return <TrendingUp className="w-4 h-4 text-green-400" />;
    if (ctr > 2) return <Minus className="w-4 h-4 text-yellow-400" />;
    return <TrendingDown className="w-4 h-4 text-red-400" />;
  };

  return (
    <div className="space-y-6">
      {/* Header z statystykami */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <motion.div 
          whileHover={{ scale: 1.02 }}
          className="p-4 rounded-xl bg-gradient-to-br from-purple-900/30 to-purple-700/30 backdrop-blur-xl border border-purple-500/20"
        >
          <div className="flex items-center justify-between mb-2">
            <Target className="w-5 h-5 text-purple-400" />
            <span className="text-xs text-purple-300">Słowa kluczowe</span>
          </div>
          <p className="text-2xl font-bold text-white">{stats.total}</p>
          <p className="text-xs text-white/60 mt-1">
            {stats.enabled} aktywnych
          </p>
        </motion.div>

        <motion.div 
          whileHover={{ scale: 1.02 }}
          className="p-4 rounded-xl bg-gradient-to-br from-green-900/30 to-green-700/30 backdrop-blur-xl border border-green-500/20"
        >
          <div className="flex items-center justify-between mb-2">
            <MousePointer className="w-5 h-5 text-green-400" />
            <span className="text-xs text-green-300">Kliknięcia</span>
          </div>
          <p className="text-2xl font-bold text-white">{stats.totalClicks.toLocaleString('pl-PL')}</p>
          <p className="text-xs text-white/60 mt-1">
            CTR: {stats.avgCtr.toFixed(2)}%
          </p>
        </motion.div>

        <motion.div 
          whileHover={{ scale: 1.02 }}
          className="p-4 rounded-xl bg-gradient-to-br from-blue-900/30 to-blue-700/30 backdrop-blur-xl border border-blue-500/20"
        >
          <div className="flex items-center justify-between mb-2">
            <DollarSign className="w-5 h-5 text-blue-400" />
            <span className="text-xs text-blue-300">Koszt</span>
          </div>
          <p className="text-2xl font-bold text-white">{stats.totalCost.toFixed(2)} PLN</p>
          <p className="text-xs text-white/60 mt-1">
            Śr. CPC: {stats.avgCpc.toFixed(2)} PLN
          </p>
        </motion.div>

        <motion.div 
          whileHover={{ scale: 1.02 }}
          className="p-4 rounded-xl bg-gradient-to-br from-pink-900/30 to-pink-700/30 backdrop-blur-xl border border-pink-500/20"
        >
          <div className="flex items-center justify-between mb-2">
            <Zap className="w-5 h-5 text-pink-400" />
            <span className="text-xs text-pink-300">Konwersje</span>
          </div>
          <p className="text-2xl font-bold text-white">{stats.totalConversions}</p>
          <p className="text-xs text-white/60 mt-1">
            CPA: {stats.totalConversions ? (stats.totalCost / stats.totalConversions).toFixed(2) : '0'} PLN
          </p>
        </motion.div>

        <motion.div 
          whileHover={{ scale: 1.02 }}
          className="p-4 rounded-xl bg-gradient-to-br from-yellow-900/30 to-yellow-700/30 backdrop-blur-xl border border-yellow-500/20"
        >
          <div className="flex items-center justify-between mb-2">
            <AlertTriangle className="w-5 h-5 text-yellow-400" />
            <span className="text-xs text-yellow-300">Wstrzymane</span>
          </div>
          <p className="text-2xl font-bold text-white">{stats.paused}</p>
          <p className="text-xs text-white/60 mt-1">
            {((stats.paused / stats.total) * 100).toFixed(0)}% wszystkich
          </p>
        </motion.div>
      </div>

      {/* Toolbar */}
      <div className="flex flex-wrap gap-4 items-center justify-between p-4 rounded-xl bg-white/5 backdrop-blur-xl border border-white/10">
        <div className="flex items-center gap-3 flex-1">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
            <input
              type="text"
              placeholder="Szukaj słów kluczowych..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 rounded-lg bg-white/10 border border-white/20 text-white placeholder-white/40 focus:outline-none focus:border-purple-500"
            />
          </div>
          
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value as any)}
            className="px-4 py-2 rounded-lg bg-white/10 border border-white/20 text-white focus:outline-none focus:border-purple-500"
          >
            <option value="all">Wszystkie</option>
            <option value="enabled">Aktywne</option>
            <option value="paused">Wstrzymane</option>
          </select>
          
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as any)}
            className="px-4 py-2 rounded-lg bg-white/10 border border-white/20 text-white focus:outline-none focus:border-purple-500"
          >
            <option value="impressions">Wyświetlenia</option>
            <option value="clicks">Kliknięcia</option>
            <option value="ctr">CTR</option>
            <option value="cost">Koszt</option>
            <option value="conversions">Konwersje</option>
          </select>
          
          <button
            onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
            className="p-2 rounded-lg bg-white/10 border border-white/20 text-white hover:bg-white/20 transition-colors"
          >
            {sortOrder === 'asc' ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
          </button>
        </div>
        
        <div className="flex items-center gap-2">
          {selectedKeywords.size > 0 && (
            <>
              <button
                onClick={bulkEnable}
                className="px-4 py-2 rounded-lg bg-green-600/20 border border-green-500/30 text-green-400 hover:bg-green-600/30 transition-colors flex items-center gap-2"
              >
                <Play className="w-4 h-4" />
                Włącz ({selectedKeywords.size})
              </button>
              <button
                onClick={bulkPause}
                className="px-4 py-2 rounded-lg bg-yellow-600/20 border border-yellow-500/30 text-yellow-400 hover:bg-yellow-600/30 transition-colors flex items-center gap-2"
              >
                <Pause className="w-4 h-4" />
                Wstrzymaj ({selectedKeywords.size})
              </button>
            </>
          )}
          
          <button
            onClick={exportKeywords}
            className="px-4 py-2 rounded-lg bg-blue-600/20 border border-blue-500/30 text-blue-400 hover:bg-blue-600/30 transition-colors flex items-center gap-2"
          >
            <Download className="w-4 h-4" />
            Eksport CSV
          </button>
          
          <button
            onClick={loadKeywords}
            disabled={loading}
            className="px-4 py-2 rounded-lg bg-purple-600/20 border border-purple-500/30 text-purple-400 hover:bg-purple-600/30 transition-colors flex items-center gap-2"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Odśwież
          </button>
        </div>
      </div>

      {/* Tabela słów kluczowych */}
      <div className="rounded-xl bg-white/5 backdrop-blur-xl border border-white/10 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-white/10 border-b border-white/10">
              <tr>
                <th className="px-4 py-3 text-left">
                  <input
                    type="checkbox"
                    checked={selectedKeywords.size === filteredAndSortedKeywords.length && filteredAndSortedKeywords.length > 0}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedKeywords(new Set(filteredAndSortedKeywords.map(k => k.id)));
                      } else {
                        setSelectedKeywords(new Set());
                      }
                    }}
                    className="rounded border-white/30"
                  />
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-white/70 uppercase tracking-wider">
                  Słowo kluczowe
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-white/70 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-white/70 uppercase tracking-wider">
                  Stawka
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-white/70 uppercase tracking-wider">
                  Quality Score
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-white/70 uppercase tracking-wider">
                  Wyświetlenia
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-white/70 uppercase tracking-wider">
                  Kliknięcia
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-white/70 uppercase tracking-wider">
                  CTR
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-white/70 uppercase tracking-wider">
                  Śr. CPC
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-white/70 uppercase tracking-wider">
                  Koszt
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-white/70 uppercase tracking-wider">
                  Konwersje
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium text-white/70 uppercase tracking-wider">
                  Akcje
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {loading ? (
                <tr>
                  <td colSpan={12} className="px-4 py-8 text-center text-white/40">
                    <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2" />
                    Ładowanie słów kluczowych...
                  </td>
                </tr>
              ) : filteredAndSortedKeywords.length === 0 ? (
                <tr>
                  <td colSpan={12} className="px-4 py-8 text-center text-white/40">
                    Brak słów kluczowych do wyświetlenia
                  </td>
                </tr>
              ) : (
                filteredAndSortedKeywords.map((keyword) => (
                  <motion.tr
                    key={keyword.id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="hover:bg-white/5 transition-colors"
                  >
                    <td className="px-4 py-3">
                      <input
                        type="checkbox"
                        checked={selectedKeywords.has(keyword.id)}
                        onChange={(e) => {
                          const newSelected = new Set(selectedKeywords);
                          if (e.target.checked) {
                            newSelected.add(keyword.id);
                          } else {
                            newSelected.delete(keyword.id);
                          }
                          setSelectedKeywords(newSelected);
                        }}
                        className="rounded border-white/30"
                      />
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div>
                          <p className="text-sm font-medium text-white">{keyword.text}</p>
                          <p className="text-xs text-white/40">{keyword.matchType}</p>
                        </div>
                        {getPerformanceIcon(keyword.ctr)}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                        keyword.status === 'ENABLED' 
                          ? 'bg-green-500/20 text-green-300 border border-green-500/30' 
                          : 'bg-yellow-500/20 text-yellow-300 border border-yellow-500/30'
                      }`}>
                        {keyword.status === 'ENABLED' ? (
                          <>
                            <CheckCircle className="w-3 h-3 mr-1" />
                            Aktywne
                          </>
                        ) : (
                          <>
                            <Pause className="w-3 h-3 mr-1" />
                            Wstrzymane
                          </>
                        )}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {editingKeyword === keyword.id ? (
                        <div className="flex items-center gap-2">
                          <input
                            type="number"
                            value={newBid}
                            onChange={(e) => setNewBid(parseFloat(e.target.value))}
                            className="w-20 px-2 py-1 rounded bg-white/10 border border-white/20 text-white text-sm"
                            step="0.1"
                          />
                          <button
                            onClick={() => updateKeywordBid(keyword, newBid)}
                            className="p-1 rounded bg-green-500/20 hover:bg-green-500/30 text-green-400"
                            disabled={actionLoading === keyword.id}
                          >
                            <CheckCircle className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => setEditingKeyword(null)}
                            className="p-1 rounded bg-red-500/20 hover:bg-red-500/30 text-red-400"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-white">{keyword.bid.toFixed(2)} PLN</span>
                          <button
                            onClick={() => {
                              setEditingKeyword(keyword.id);
                              setNewBid(keyword.bid);
                            }}
                            className="p-1 rounded hover:bg-white/10"
                          >
                            <Edit2 className="w-3 h-3 text-white/40" />
                          </button>
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`font-medium ${getQualityScoreColor(keyword.qualityScore)}`}>
                        {keyword.qualityScore}/10
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-white/80">
                      {keyword.impressions.toLocaleString('pl-PL')}
                    </td>
                    <td className="px-4 py-3 text-sm text-white/80">
                      {keyword.clicks.toLocaleString('pl-PL')}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-white/80">{keyword.ctr.toFixed(2)}%</span>
                        <div className="w-16 h-1 bg-white/10 rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-gradient-to-r from-purple-500 to-pink-500"
                            style={{ width: `${Math.min(100, keyword.ctr * 10)}%` }}
                          />
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-white/80">
                      {keyword.avgCpc.toFixed(2)} PLN
                    </td>
                    <td className="px-4 py-3 text-sm text-white/80">
                      {keyword.cost.toFixed(2)} PLN
                    </td>
                    <td className="px-4 py-3">
                      <div>
                        <p className="text-sm text-white/80">{keyword.conversions}</p>
                        <p className="text-xs text-white/40">{keyword.conversionRate.toFixed(2)}%</p>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-center gap-1">
                        <button
                          onClick={() => toggleKeywordStatus(keyword)}
                          className={`p-1.5 rounded-lg transition-colors ${
                            keyword.status === 'ENABLED'
                              ? 'bg-yellow-500/20 hover:bg-yellow-500/30 text-yellow-400'
                              : 'bg-green-500/20 hover:bg-green-500/30 text-green-400'
                          }`}
                          disabled={actionLoading === keyword.id}
                          title={keyword.status === 'ENABLED' ? 'Wstrzymaj' : 'Włącz'}
                        >
                          {actionLoading === keyword.id ? (
                            <RefreshCw className="w-4 h-4 animate-spin" />
                          ) : keyword.status === 'ENABLED' ? (
                            <Pause className="w-4 h-4" />
                          ) : (
                            <Play className="w-4 h-4" />
                          )}
                        </button>
                        <button
                          onClick={() => addNegativeKeyword(keyword.text)}
                          className="p-1.5 rounded-lg bg-red-500/20 hover:bg-red-500/30 text-red-400 transition-colors"
                          title="Dodaj jako negatywne"
                        >
                          <Shield className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </motion.tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Legenda */}
      <div className="flex flex-wrap gap-4 p-4 rounded-xl bg-white/5 backdrop-blur-xl border border-white/10">
        <div className="flex items-center gap-2 text-xs text-white/60">
          <TrendingUp className="w-4 h-4 text-green-400" />
          <span>CTR &gt; 5% (wysoka wydajność)</span>
        </div>
        <div className="flex items-center gap-2 text-xs text-white/60">
          <Minus className="w-4 h-4 text-yellow-400" />
          <span>CTR 2-5% (średnia wydajność)</span>
        </div>
        <div className="flex items-center gap-2 text-xs text-white/60">
          <TrendingDown className="w-4 h-4 text-red-400" />
          <span>CTR &lt; 2% (niska wydajność)</span>
        </div>
        <div className="flex items-center gap-2 text-xs text-white/60">
          <Shield className="w-4 h-4 text-red-400" />
          <span>Dodaj jako negatywne słowo</span>
        </div>
      </div>
    </div>
  );
};

export default KeywordsManager;
